import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axios from "axios";

import { showNotification } from "../../../helpers/ShowNotification";
import {
  mapPriceItem,
  mergePriceOptions,
  buildSavedPriceOption,
  getSellingPriceFromSelection,
  appendVariationPriceToFormData,
  getCarModelVariationsFromCar,
  EDIT_CAR_FORM_EXCLUDED_KEYS,
} from "./carVariationPriceUtils";
import { getAllBrands } from "../../../redux/CarsManagement/Brands/brandsSlice";
import {
  editCar,
  patchEditCarFormValues,
} from "../../../redux/CarsManagement/Cars/carsSlice";
import CarsEffects from "../../../redux/CarsManagement/Cars/carsEffects";
import { getAllBrandTypes } from "../../../redux/CarsManagement/BrandTypes/brandTypesSlice";
import { colorOptions, manufacturingYears } from "../../../helpers/Utilities";
import LoadingOverlay from "../../../helpers/LoadingOverlay/LoadingOverlay";
import StatusSelect from "../../../components/CustomSelect/StatusSelect";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import CustomInput from "../../../components/CustomInput/CustomInput";
import { base_url } from "../../../helpers/Constants";

function EditCar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { editCarFormValues, isLoading } = useSelector((state) => state.cars);
  const { allBrands, isLoading: brandsLoading } = useSelector(
    (state) => state.brands
  );
  const {
    allBrandTypes,
    isLoading: brandTypesLoading,
  } = useSelector((state) => state.brandTypes);

  // =========================================================
  // STATES
  // =========================================================
  const [image, setImage] = useState();

  // Single variation selection
  const [selectedVariation, setSelectedVariation] = useState(null);

  const [allLoadedVariations, setAllLoadedVariations] = useState([]);
  const [variationsLoading, setVariationsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");

  // Price list for the currently selected variation
  const [priceListsByVariationId, setPriceListsByVariationId] = useState({});

  // Selected price (single object)
  const [selectedPrice, setSelectedPrice] = useState(null);

  // Uploaded Excel file for the variation
  const [variationFile, setVariationFile] = useState(null);

  const [variationPricesDirty, setVariationPricesDirty] = useState(false);

  // =========================================================
  // CHECK DATA
  // =========================================================
  useEffect(() => {
    if (!editCarFormValues) {
      navigate("/Cars");
    }
  }, [editCarFormValues]);

  // =========================================================
  // OPTIONS
  // =========================================================
  const manufacturingOptions = manufacturingYears();

  const brandOptions = allBrands.map((brand) => ({
    value: brand.id,
    label: brand.nameAr,
  }));

  const brandTypesOptions = allBrandTypes.map((brandType) => ({
    value: brandType.id,
    label: brandType.nameAr,
  }));

  // =========================================================
  // FORM
  // =========================================================
  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: editCarFormValues,
  });

  const selectedBrandId = watch("brand_id");
  const selectedBrandTypeId = watch("brandType_id");

  useEffect(() => {
    reset(editCarFormValues);
  }, [editCarFormValues]);

  // =========================================================
  // IMAGE
  // =========================================================
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(URL.createObjectURL(e.target.files[0]));
    }
  };

  // =========================================================
  // GET BRANDS
  // =========================================================
  useEffect(() => {
    dispatch(getAllBrands());
  }, [dispatch]);

  // =========================================================
  // GET BRAND TYPES
  // =========================================================
  const handleBrandTypes = (brandId) => {
    if (brandId) {
      dispatch(getAllBrandTypes(brandId));
    }
  };

  useEffect(() => {
    if (editCarFormValues?.brand_id) {
      dispatch(getAllBrandTypes(editCarFormValues.brand_id));
    }
  }, [editCarFormValues]);

  // Infer brand_id from type when API does not return it
  useEffect(() => {
    if (!editCarFormValues?.brandType_id || !allBrandTypes.length) return;
    if (selectedBrandId) return;

    const brandType = allBrandTypes.find(
      (x) => x.id == editCarFormValues.brandType_id
    );
    if (brandType?.brand_id) {
      setValue("brand_id", brandType.brand_id, { shouldDirty: false });
    }
  }, [editCarFormValues, allBrandTypes, selectedBrandId, setValue]);

  const prevBrandSelectionRef = useRef({ brandId: null, brandTypeId: null });
  const carDetailsFetchedRef = useRef(null);

  // =========================================================
  // RESET VARIATIONS / PRICES ON BRAND CHANGE
  // =========================================================
  useEffect(() => {
    if (!selectedBrandId || !selectedBrandTypeId) return;

    const prev = prevBrandSelectionRef.current;
    const isUserChange =
      prev.brandId != null &&
      (prev.brandId !== selectedBrandId ||
        prev.brandTypeId !== selectedBrandTypeId);

    prevBrandSelectionRef.current = {
      brandId: selectedBrandId,
      brandTypeId: selectedBrandTypeId,
    };

    if (!isUserChange) return;

    setSelectedVariation(null);
    setAllLoadedVariations([]);
    setPage(1);
    setHasMore(true);
    setSearch("");
    setPriceListsByVariationId({});
    setSelectedPrice(null);
    setVariationFile(null);
    setVariationPricesDirty(false);
  }, [selectedBrandId, selectedBrandTypeId]);

  // =========================================================
  // FETCH PRICE LIST
  // =========================================================
  const fetchPriceList = useCallback(
    async (modelVariationId, page = 1, append = false) => {
      setPriceListsByVariationId((prev) => ({
        ...prev,
        [modelVariationId]: {
          items: append ? prev[modelVariationId]?.items ?? [] : [],
          pageNumber: page,
          hasMore: prev[modelVariationId]?.hasMore ?? false,
          loading: true,
        },
      }));

      try {
        const response = await axios.get(
          "/Cars/get-price-list-by-model-code",
          {
            params: {
              ModelVariationId: modelVariationId,
              PageNumber: page,
              PageSize: 100,
            },
          }
        );

        const items = response?.data?.data?.items || [];
        const mappedItems = items.map(mapPriceItem);

        setPriceListsByVariationId((prev) => {
          const existing = append ? prev[modelVariationId]?.items ?? [] : [];
          return {
            ...prev,
            [modelVariationId]: {
              items: mergePriceOptions(existing, mappedItems),
              pageNumber: page,
              hasMore: items.length >= 100,
              loading: false,
            },
          };
        });
      } catch (error) {
        console.log(error);
        setPriceListsByVariationId((prev) => ({
          ...prev,
          [modelVariationId]: {
            items: append ? prev[modelVariationId]?.items ?? [] : [],
            pageNumber: page,
            hasMore: false,
            loading: false,
          },
        }));
      }
    },
    []
  );

  // =========================================================
  // HANDLE SINGLE VARIATION CHANGE
  // =========================================================
  const handleVariationChange = (newValue) => {
    // newValue is { value, label } or null
    const prevId = selectedVariation?.value;
    const newId = newValue?.value;

    if (prevId === newId) return;

    setPriceListsByVariationId({});
    setSelectedPrice(null);
    setVariationFile(null);

    if (newId) {
      fetchPriceList(newId, 1, false);
    }

    setSelectedVariation(newValue);
    setVariationPricesDirty(true);
  };

  const handlePriceSelect = (option) => {
    setSelectedPrice(option);
    setVariationPricesDirty(true);
  };

  const handleVariationFileChange = (file) => {
    setVariationFile(file);
    setVariationPricesDirty(true);
  };

  const handleLoadMorePrices = () => {
    const variationId = selectedVariation?.value;
    if (!variationId) return;

    const priceList = priceListsByVariationId[variationId];
    if (!priceList || priceList.loading || !priceList.hasMore) return;

    fetchPriceList(variationId, priceList.pageNumber + 1, true);
  };

  // =========================================================
  // INITIAL VARIATIONS – load first existing variation
  // =========================================================
  useEffect(() => {
    if (!editCarFormValues?.id) return;
    if (carDetailsFetchedRef.current === editCarFormValues.id) return;

    const existingVariations = getCarModelVariationsFromCar(editCarFormValues);
    if (existingVariations.length > 0) return;

    carDetailsFetchedRef.current = editCarFormValues.id;

    const loadCarDetails = async () => {
      try {
        const response = await CarsEffects.getCarById(editCarFormValues.id);
        const car = response?.data ?? response;
        if (car) {
          dispatch(
            patchEditCarFormValues({
              ...editCarFormValues,
              ...car,
            })
          );
        }
      } catch (error) {
        console.log(error);
        carDetailsFetchedRef.current = null;
      }
    };

    loadCarDetails();
  }, [dispatch, editCarFormValues]);

  useEffect(() => {
    const modelVariations = getCarModelVariationsFromCar(editCarFormValues);
    if (!modelVariations.length) return;

    // Take only the first variation
    const item = modelVariations[0];

    const option = {
      value: item.id,
      label: item.description ?? item.arDescription ?? "",
      priceListNumber: item.priceListNumber ?? item.PriceListNumber,
      sellingPrice: item.sellingPrice ?? item.SellingPrice,
    };

    setSelectedVariation({ value: option.value, label: option.label });

    setAllLoadedVariations((prev) => {
      const exists = prev.some((v) => v.value === option.value);
      if (!exists) {
        return [...prev, { value: option.value, label: option.label }];
      }
      return prev;
    });

    const savedOption = buildSavedPriceOption(option);
    if (savedOption) {
      setSelectedPrice(savedOption);
    }

    fetchPriceList(option.value, 1, false);
    setVariationPricesDirty(false);
  }, [editCarFormValues, fetchPriceList]);

  // =========================================================
  // FETCH VARIATIONS (async search)
  // =========================================================
  const fetchVariations = useCallback(
    async ({ page = 1, search = "", append = false }) => {
      try {
        const brand = allBrands.find((x) => x.id == selectedBrandId);
        const model = allBrandTypes.find((x) => x.id == selectedBrandTypeId);
        if (!brand || !model) return [];

        setVariationsLoading(true);

        const response = await axios.get(`/Cars/get-all-model-variations`, {
          params: {
            pageNumber: page,
            pageSize: 100,
            ManufactoryName: brand.nameEn,
            ModelCodeName: model.nameEn,
            Search: search,
          },
        });

        const data = response?.data?.data;
        const items = data?.items || [];
        setHasMore(items.length > 0);

        const uniqueItems = [
          ...new Map(items.map((item) => [item.modelVariationId, item])).values(),
        ];

        const mappedItems = uniqueItems.map((item) => ({
          value: item.modelVariationId,
          label: item.arDescription,
          enDescription: item.enDescription,
        }));

        if (append) {
          setAllLoadedVariations((prev) => {
            const merged = [...prev, ...mappedItems];
            return [...new Map(merged.map((item) => [item.value, item])).values()];
          });
        } else {
          setAllLoadedVariations(mappedItems);
        }

        return mappedItems;
      } catch (error) {
        console.log(error);
        return [];
      } finally {
        setVariationsLoading(false);
      }
    },
    [allBrands, allBrandTypes, selectedBrandId, selectedBrandTypeId]
  );

  // Load first page when brand/model change
  useEffect(() => {
    if (!selectedBrandId || !selectedBrandTypeId || !allBrands.length || !allBrandTypes.length) return;

    const brand = allBrands.find((x) => x.id == selectedBrandId);
    const model = allBrandTypes.find((x) => x.id == selectedBrandTypeId);
    if (!brand || !model) return;

    setPage(1);
    fetchVariations({ page: 1, search: "", append: false });
  }, [selectedBrandId, selectedBrandTypeId, allBrands, allBrandTypes, fetchVariations]);

  const handleVariationsMenuOpen = () => {
    if (!selectedBrandId || !selectedBrandTypeId) return;
    fetchVariations({
      page: 1,
      search: search.length >= 2 ? search : "",
      append: false,
    });
  };

  const loadOptions = async (inputValue) => {
    setSearch(inputValue);
    if (inputValue.length >= 2) {
      setPage(1);
      return await fetchVariations({
        page: 1,
        search: inputValue,
        append: false,
      });
    }
    return allLoadedVariations;
  };

  const handleMenuScrollToBottom = async () => {
    if (variationsLoading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchVariations({
      page: nextPage,
      search,
      append: true,
    });
  };

  // =========================================================
  // SUBMIT
  // =========================================================
  const handleEditCar = (data) => {
    if (!selectedVariation) {
      showNotification({
        message: "يرجى اختيار موديل واحد على الأقل",
        status: "warning",
      });
      return;
    }

    const sellingPrice = getSellingPriceFromSelection(selectedPrice);
    if (!sellingPrice) {
      showNotification({
        message: "يرجى اختيار سعر للموديل",
        status: "warning",
      });
      return;
    }

    const formData = new FormData();

    // Colors
    const colors = data.colors || [];
    colors.forEach((color, index) => {
      formData.append(`colors[${index}].code`, color);
    });

    // Single model variation
    formData.append("ModelVariation[0].id", selectedVariation.value);
    formData.append("ModelVariation[0].arDescription", selectedVariation.label);
    appendVariationPriceToFormData(formData, 0, selectedPrice);

    if (variationFile) {
      formData.append("ModelVariation[0].File", variationFile);
    }

    // Other data
    for (let key in data) {
      if (EDIT_CAR_FORM_EXCLUDED_KEYS.includes(key) || key === "ModelVariation") continue;
      if (key === "imageProfile") {
        if (data[key]?.[0] instanceof File) {
          formData.append(key, data[key][0]);
        }
        continue;
      }
      const value = data[key];
      if (value === null || value === undefined || typeof value === "object") continue;
      formData.append(key, value);
    }

    dispatch(editCar({ formData, id: data.id })).then(
      (res) => !res.error && navigate("/Cars")
    );
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      <nav className="uk-position-relative" aria-label="Breadcrumb">
        <ul className="uk-breadcrumb">
          <li>
            <NavLink to="/Cars">السيارات</NavLink>
          </li>
          <li>
            <span aria-current="page">تعديل سيارة</span>
          </li>
        </ul>
      </nav>

      {brandsLoading || brandTypesLoading ? (
        <LoadingOverlay />
      ) : (
        <div className="uk-padding-small uk-background-default uk-border-rounded">
          <form className="uk-grid-small uk-child-width-1-2" uk-grid="">
            {/* NAME AR */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الإسم بالعربي</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="nameAr" error={errors.nameAr} />
              </div>
            </div>

            {/* NAME EN */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الإسم بالإنجليزي</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="nameEn" error={errors.nameEn} />
              </div>
            </div>

            {/* DESCRIPTION AR */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الوصف بالعربي</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="descriptionAr" error={errors.descriptionAr} />
              </div>
            </div>

            {/* DESCRIPTION EN */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الوصف بالإنجليزي</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="descriptionEn" error={errors.descriptionEn} />
              </div>
            </div>

            {/* OVERVIEW AR */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">نظرة عامة بالعربي</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="overviewAr" error={errors.overviewAr} />
              </div>
            </div>

            {/* OVERVIEW EN */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">نظرة عامة بالإنجليزي</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="overviewEn" error={errors.overviewEn} />
              </div>
            </div>

            {/* BRAND */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">المُصنع</label>
              <div className="uk-form-controls uk-margin-small-top">
                <Controller
                  name="brand_id"
                  control={control}
                  rules={{ required: "هذا الحقل مطلوب!" }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="اختر مُصنع"
                      field={field}
                      options={brandOptions}
                      error={errors.brand_id}
                      onChange={(val) => {
                        field.onChange(val);
                        handleBrandTypes(val);
                      }}
                    />
                  )}
                />
              </div>
            </div>

            {/* BRAND TYPE */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">النوع</label>
              <div className="uk-form-controls uk-margin-small-top">
                <Controller
                  name="brandType_id"
                  control={control}
                  rules={{ required: "هذا الحقل مطلوب!" }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="اختر نوع"
                      field={field}
                      options={brandTypesOptions}
                      error={errors.brandType_id}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </div>
            </div>

            {/* MODEL VARIATION – single select */}
            <div className="uk-width-1-1">
              <label className="uk-form-label uk-text-gray uk-text-bold">الموديل</label>
              <div className="uk-form-controls uk-margin-small-top">
                <AsyncSelect
                  cacheOptions
                  defaultOptions={allLoadedVariations}
                  loadOptions={loadOptions}
                  value={selectedVariation}
                  onChange={handleVariationChange}
                  onMenuOpen={handleVariationsMenuOpen}
                  onMenuScrollToBottom={handleMenuScrollToBottom}
                  isLoading={variationsLoading}
                  placeholder="ابحث عن موديل (حرفين على الأقل للبحث)"
                  noOptionsMessage={() => "لا توجد نتائج"}
                  loadingMessage={() => "جاري التحميل..."}
                  isDisabled={!selectedBrandId || !selectedBrandTypeId}
                  styles={{ menuList: (base) => ({ ...base, maxHeight: 250, overflowY: "auto" }) }}
                />
              </div>

              {selectedVariation && (
                <div className="uk-margin-top">
                  <h5 className="uk-text-bold">الموديل المختار</h5>
                  <span className="uk-label">
                    {selectedVariation.label}
                    {selectedPrice && (
                      <span className="uk-margin-small-right">
                        {" "}
                        — {getSellingPriceFromSelection(selectedPrice)}
                      </span>
                    )}
                  </span>

                  <div className="uk-margin-top">
                    <h5 className="uk-text-bold">اختيار السعر</h5>
                    <div className="uk-margin-small-top uk-padding-small uk-background-muted uk-border-rounded">
                      <label className="uk-form-label uk-text-bold">{selectedVariation.label}</label>
                      {selectedPrice && (
                        <p className="uk-text-small uk-text-muted uk-margin-remove-top">
                          السعر الحالي: {getSellingPriceFromSelection(selectedPrice)}
                        </p>
                      )}

                      {/* Excel file */}
                      <div className="uk-margin-small-top">
                        <label className="uk-form-label">ملف الإكسيل</label>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          className="uk-input"
                          onChange={(e) => handleVariationFileChange(e.target.files?.[0])}
                        />
                        {variationFile && (
                          <div className="uk-text-success uk-margin-small-top">
                            {variationFile.name}
                          </div>
                        )}
                      </div>

                      <div className="uk-margin-small-top">
                        {priceListsByVariationId[selectedVariation.value]?.loading &&
                        !priceListsByVariationId[selectedVariation.value]?.items?.length ? (
                          <span className="uk-text-muted">
                            <span uk-spinner="ratio: 0.6"></span> جاري تحميل الأسعار...
                          </span>
                        ) : !priceListsByVariationId[selectedVariation.value]?.items?.length ? (
                          <span className="uk-text-muted">لا توجد أسعار متاحة</span>
                        ) : (
                          <>
                            <Select
                              options={priceListsByVariationId[selectedVariation.value].items}
                              value={selectedPrice}
                              onChange={handlePriceSelect}
                              placeholder="اختر السعر"
                              isLoading={priceListsByVariationId[selectedVariation.value]?.loading}
                            />
                            {priceListsByVariationId[selectedVariation.value]?.hasMore && (
                              <button
                                type="button"
                                className="uk-button uk-button-default uk-button-small uk-margin-small-top"
                                disabled={priceListsByVariationId[selectedVariation.value].loading}
                                onClick={handleLoadMorePrices}
                              >
                                {priceListsByVariationId[selectedVariation.value].loading
                                  ? "جاري التحميل..."
                                  : "تحميل المزيد"}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ORDER */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الترتيب</label>
              <div className="uk-form-controls uk-margin-small-top">
                <CustomInput control={control} name="order" error={errors.order} type="number" />
              </div>
            </div>

            {/* STATUS */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الحالة</label>
              <div className="uk-form-controls uk-margin-small-top">
                <Controller
                  name="status"
                  control={control}
                  rules={{ validate: (v) => typeof v === "boolean" || "هذا الحقل مطلوب!" }}
                  render={({ field }) => <StatusSelect field={field} error={errors.status} />}
                />
              </div>
            </div>

            {/* YEAR */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">سنة الصنع</label>
              <div className="uk-form-controls uk-margin-small-top">
                <Controller
                  name="manufacture_year"
                  control={control}
                  rules={{ required: "هذا الحقل مطلوب!" }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="اختر السنة"
                      field={field}
                      options={manufacturingOptions}
                      error={errors.manufacture_year}
                    />
                  )}
                />
              </div>
            </div>

            {/* IMAGE */}
            <div>
              <label className="uk-form-label uk-text-gray uk-text-bold">الصورة</label>
              <div className="uk-form-controls uk-margin-small-top">
                <div uk-form-custom="">
                  <input
                    type="file"
                    aria-label="Custom controls"
                    name="imageProfile"
                    {...register("imageProfile", { onChange: handleImageChange })}
                  />
                  <button
                    className="uk-button uk-button-default uk-border-rounded"
                    type="button"
                    tabIndex="-1"
                  >
                    إختر صورة
                  </button>
                  <img
                    src={image ?? `${base_url}/${editCarFormValues?.imageProfile}`}
                    alt="Selected"
                    className="uk-height-max-small uk-border-rounded uk-margin-small-right uk-align-left"
                  />
                </div>
              </div>
            </div>

            {/* COLORS */}
            <h4 className="uk-heading-line uk-margin-medium-top uk-margin-small-bottom uk-width-1-1 uk-text-bold">
              <span>الألوان</span>
            </h4>
            {colorOptions.map((color) => (
              <div key={color.id} className="uk-form-controls">
                <input
                  className="uk-checkbox"
                  type="checkbox"
                  value={color.value}
                  {...register("colors")}
                  defaultChecked={editCarFormValues?.colors?.some(
                    (item) => item.code === color.value
                  )}
                />
                <span
                  className="uk-badge uk-margin-small-left uk-box-shadow-small"
                  style={{ backgroundColor: color.value }}
                ></span>
                {color.label}
              </div>
            ))}
          </form>

          {/* ACTIONS */}
          <div className="uk-margin-top uk-text-left">
            <button
              className="uk-button uk-button-secondary uk-margin-small-left uk-border-rounded"
              type="button"
              disabled={(!isDirty && !variationPricesDirty) || isLoading}
              onClick={handleSubmit(handleEditCar)}
            >
              {isLoading ? <div uk-spinner="ratio: 0.5"></div> : "تعديل"}
            </button>
            <NavLink
              to="/Cars"
              className="uk-button uk-button-primary uk-modal-close uk-border-rounded"
              type="button"
            >
              إلغاء
            </NavLink>
          </div>
        </div>
      )}
    </>
  );
}

export default EditCar;